import { z } from 'zod';
/** Set above the largest current all-organization response so existing callers are not truncated. */
export const CONNECTION_LIST_LIMIT_MAX = 2_000;
/** The highest `totalCount` reported; a count at the cap means at least this many matched. */
export const CONNECTION_LIST_TOTAL_COUNT_MAX = 10_000;
/** The kind of integration a connection represents. */
export var ConnectionType;
(function (ConnectionType) {
    ConnectionType["Sftp"] = "Sftp";
    ConnectionType["Api"] = "Api";
    ConnectionType["InterfaceEngine"] = "InterfaceEngine";
    ConnectionType["IntegrationEngine"] = "IntegrationEngine";
})(ConnectionType || (ConnectionType = {}));
/**
 * One integration connection in the merged GET /connections view, drawn from four sources: SFTP
 * feeds, API instances, interface engine (InterfaceEngine) connections, and newer SFTP connections
 * from Resource Gateway. The fields are the common shape those sources are projected onto — nothing
 * here is specific to retrieving a single connection.
 *
 * `detailPath` from the internal row is deliberately omitted: it addresses per-type detail routes
 * that are not part of this contract. The route still sends it for the staff UI.
 */
export const ConnectionSchema = z.object({
    connectionType: z
        .enum(ConnectionType)
        .describe('The kind of connection: Sftp, Api, or InterfaceEngine. IntegrationEngine is retired and no ' +
        'longer returned.'),
    connectionId: z.string().describe('Identifier for this connection within its source system.'),
    name: z.string().describe('Human-readable connection name.'),
    source: z
        .string()
        .describe('A per-source label for where the data comes from: the sending system, API kind, or SFTP ' +
        'folder. Empty for newer SFTP connections and may be empty for others, so a source ' +
        'filter can omit connections.'),
    destination: z
        .string()
        .describe('Always Notable: every listed connection carries data into Notable.'),
    method: z
        .string()
        .describe('Transport or integration method, e.g. SFTP, Api, MLLP, HTTP, File, or Interface Engine.'),
    dataType: z
        .string()
        .describe('Payload format, e.g. FHIR, CSV, HL7V2, or UnstructuredFile; not an HL7 event type.'),
    organizationId: z.string().describe('Identifier of the organization that owns this connection.'),
    lastUpdated: z.iso
        .datetime()
        .describe('ISO-8601 timestamp of when the connection configuration was last changed. For ' +
        'InterfaceEngine connections this is served from a cache and may be about an hour stale.'),
    lastActivityAt: z.iso
        .datetime()
        .nullable()
        .describe("ISO-8601 timestamp of the connection's most recent data activity, or null when unknown."),
    fetchedAt: z.iso
        .datetime()
        .nullable()
        .describe('ISO-8601 timestamp of when cached InterfaceEngine details were read; null for connection ' +
        'types fetched live on each request.'),
});
/**
 * Filters and result cap for GET /connections. The text filters are case-insensitive substring
 * matches; the org filter narrows to one organization the caller is authorized for, and
 * `connectionType` matches exactly.
 *
 * Strict so an unknown query parameter is rejected rather than silently ignored, and the text
 * filters reject empty or whitespace-only values (`.min(1)` after trimming) — both matching the
 * validation the endpoint performs today.
 */
export const ConnectionListQuerySchema = z
    .object({
    organizationId: z
        .uuid()
        .optional()
        .describe('Return only connections owned by this organization, which the caller must be authorized for.'),
    connectionType: z
        .enum(ConnectionType)
        .optional()
        .describe('Return only connections of this type.'),
    dataType: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Return only connections whose data type contains this text (case-insensitive).'),
    source: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Return only connections whose source contains this text (case-insensitive).'),
    destination: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Return only connections whose destination contains this text (case-insensitive).'),
    method: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Return only connections whose method contains this text (case-insensitive).'),
    q: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Return only connections matching this text (case-insensitive) in name, source, ' +
        'destination, method, data type, or connection type.'),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(CONNECTION_LIST_LIMIT_MAX)
        .default(CONNECTION_LIST_LIMIT_MAX)
        .describe('Maximum number of connections to return; defaults to the maximum, ' +
        `${CONNECTION_LIST_LIMIT_MAX.toString()}. Matches beyond the limit are omitted and ` +
        '`truncated` is set; narrow the query with filters rather than paging.'),
})
    .strict();
//# sourceMappingURL=connection.js.map